/**
 * Shared building rendering helpers — used by both BuildingState (editable)
 * and LabyrinthState (read-only visual).
 */
import type { Container, Texture } from 'pixi.js';
import { Assets, Graphics, Sprite, Text } from 'pixi.js';
import { ALL_BUILDINGS, getBuildingById, type BuildingId } from '../buildings/BuildingData';
import type { PlacedBuilding } from '../buildings/BuildingSystem';
import { TILE_SIZE } from '../labyrinth/Labyrinth';

/** Map from BuildingId → loaded Texture. */
export type BuildingTextureMap = Map<BuildingId, Texture>;

/**
 * Load all building SVG textures. Call once during state init.
 */
export async function loadBuildingTextures(): Promise<BuildingTextureMap> {
  const results = await Promise.all(
    ALL_BUILDINGS.map(async (def) => {
      try {
        const tex = await Assets.load<Texture>(`/maps/buildings/${def.id}.svg`);
        return [def.id, tex] as const;
      } catch {
        console.warn(`Failed to load building texture: ${def.id}`);
        return null;
      }
    }),
  );
  const entries = results.filter((e): e is NonNullable<typeof e> => e !== null);
  return new Map(entries);
}

/**
 * Rebuild building sprites in the given container using loaded textures.
 * Clears any previous children first.
 */
export function rebuildBuildingSprites(
  container: Container,
  buildings: readonly PlacedBuilding[],
  baseOriginX: number,
  baseOriginY: number,
  textures: BuildingTextureMap,
): void {
  container.removeChildren();

  const ts = TILE_SIZE;

  for (const b of buildings) {
    const def = getBuildingById(b.id);
    if (!def) continue;

    const tex = textures.get(b.id);
    if (!tex) continue;

    const wx = (baseOriginX + b.gridX) * ts;
    const wy = (baseOriginY + b.gridY) * ts;
    const bw = def.footprintW * ts;
    const bh = def.footprintH * ts;

    const sprite = new Sprite(tex);
    sprite.position.set(wx, wy);
    sprite.width = bw;
    sprite.height = bh;
    container.addChild(sprite);

    // ¾-view: south-facing front face strip below building
    const faceH = 8;
    const faceG = new Graphics();
    faceG.rect(wx, wy + bh, bw, 2);
    faceG.fill({ color: 0x2d1420, alpha: 1 }); // highlight strip
    faceG.rect(wx, wy + bh + 2, bw, faceH - 2);
    faceG.fill({ color: 0x251018, alpha: 1 }); // face body
    container.addChild(faceG);

    // Level pips (bottom-right corner)
    if (b.level > 0) {
      const pipG = new Graphics();
      const pipSize = 4;
      const pipGap = 3;
      const bodyInset = 4;
      const pipY = wy + bh - bodyInset - pipSize - 2;
      const pipStartX = wx + bw - bodyInset - (b.level * (pipSize + pipGap)) - 2;
      for (let i = 0; i < b.level; i++) {
        const pipX = pipStartX + i * (pipSize + pipGap);
        pipG.rect(pipX, pipY, pipSize, pipSize);
        pipG.fill({ color: 0xffdd44, alpha: 0.9 });
      }
      container.addChild(pipG);
    }
  }
}

/**
 * Rebuild Text labels (names only — the sprite provides the visual icon).
 * Call once on initial render and after any building change — not per-frame.
 */
export function rebuildBuildingLabels(
  container: Container,
  buildings: readonly PlacedBuilding[],
  baseOriginX: number,
  baseOriginY: number,
): void {
  container.removeChildren();

  for (const b of buildings) {
    const def = getBuildingById(b.id);
    if (!def) continue;

    const ts = TILE_SIZE;
    const wx = (baseOriginX + b.gridX) * ts;
    const wy = (baseOriginY + b.gridY) * ts;
    const bw = def.footprintW * ts;
    const bh = def.footprintH * ts;

    // Name label (if footprint large enough)
    if (bw >= ts * 2 && bh >= ts * 2) {
      const label = new Text({
        text: def.name,
        style: { fontFamily: 'Offside', fontSize: Math.min(10, ts * 0.35), fill: 0xcccccc },
      });
      label.anchor.set(0.5);
      label.position.set(wx + bw / 2, wy + bh / 2 + (bh > ts * 2 ? 4 : 2));
      container.addChild(label);
    }
  }
}

/**
 * Draw a ghost placement footprint (green = valid, red = invalid).
 */
export function drawGhostFootprint(
  g: Graphics,
  worldX: number,
  worldY: number,
  footprintW: number,
  footprintH: number,
  valid: boolean,
): void {
  const ts = TILE_SIZE;
  const px = worldX * ts;
  const py = worldY * ts;
  const bw = footprintW * ts;
  const bh = footprintH * ts;
  const color = valid ? 0x44cc44 : 0xcc4444;

  g.rect(px, py, bw, bh);
  g.fill({ color, alpha: 0.25 });
  g.rect(px, py, bw, bh);
  g.stroke({ color, width: 2, alpha: 0.7 });
}

/**
 * Convert a hex number to a CSS color string for canvas 2D rendering.
 */
export function buildingColorToCSS(color: number): string {
  return '#' + color.toString(16).padStart(6, '0');
}
